import React, { forwardRef, useCallback } from "react";
import { SUPPORTED_EXCHANGES, METRICS_EVENTS } from "@cede/types";
import { SnackbarTradeInfo } from "./SnackbarTradeInfo";
import { CONNECT_ACCOUNT_SNACKBAR_CONTENT } from "@/components/features/wallet/ConnectAccount";
import { Button } from "@/components/ui/button";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { cn } from "@/lib/utils";
import { FLOW_TYPES, STEPS, useConnectStore } from "@/store/connectStore";
import { CheckCircle, AlertCircle, Info, X, AlertTriangle } from "lucide-react";
import { CustomContentProps, SnackbarContent, SnackbarContentProps, useSnackbar } from "notistack";

export const getIcon = (variant: "success" | "error" | "info" | "trade" | string | undefined) => {
  const className = "h-6 w-6";
  switch (variant) {
    case "trade":
    case "success":
      return (
        <span className="t-success-check" data-state="in">
          <CheckCircle className={className} />
        </span>
      );
    case "error":
      return <AlertCircle className={className} />;
    case "info":
      return <Info className={className} />;
    case "warning":
      return <AlertTriangle className={className} />;
    default:
      return null;
  }
};

const getAccentColorClass = (variant: string | undefined) => {
  switch (variant) {
    case "trade":
    case "success":
      return "text-cede-success";
    case "error":
      return "text-cede-error";
    case "info":
      // Use primary brand blue for informational snackbars
      return "text-cede-primary";
    case "warning":
      return "text-cede-warning";
    default:
      return "text-cede-primary";
  }
};

const getBackgroundColorClass = (variant: string | undefined) => {
  switch (variant) {
    case "trade":
    case "success":
      return "bg-cede-success/20";
    case "error":
      return "bg-cede-error/20";
    case "info":
      // Use a blue-tinted background for informational snackbars
      return "bg-cede-primary/25";
    case "warning":
      return "bg-cede-warning/20";
    default:
      return "bg-cede-info/20";
  }
};

const getBorderColorClass = (variant: string | undefined) => {
  switch (variant) {
    case "trade":
    case "success":
      return "bg-cede-success";
    case "error":
      return "bg-cede-error";
    case "info":
      // Accent bar in primary blue for informational snackbars
      return "bg-cede-primary";
    case "warning":
      return "bg-cede-warning";
    default:
      return "bg-cede-primary";
  }
};

type SnackbarProps = CustomContentProps & {
  incorrectPermission?: boolean;
  exchangeId?: string;
  supportLinkUrl?: string;
  supportText?: string;
};

export const CustomSnackbar = forwardRef<HTMLDivElement, SnackbarProps>((props, ref) => {
  const {
    id,
    variant,
    message,
    incorrectPermission,
    exchangeId,
    supportLinkUrl,
    supportText,
    action,
    className,
    style,
  } = props;
  const { closeSnackbar } = useSnackbar();
  const { setOpenConnectModal, setActiveStep, setExchangeIds, setFlowType } = useConnectStore();
  const { trackEvent } = useMetricsEvents();

  const handleDismiss = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      closeSnackbar(id);
    },
    [closeSnackbar, id],
  );

  const handleSnackbarClick = useCallback(() => {
    if (incorrectPermission) {
      setFlowType(FLOW_TYPES.RELINK);
      setExchangeIds(exchangeId ? [exchangeId] : Object.values(SUPPORTED_EXCHANGES));
      setActiveStep(STEPS.ChooseCEX);
      setOpenConnectModal(true);
      return;
    }

    if (supportLinkUrl) {
      if (variant === "error") {
        trackEvent(METRICS_EVENTS.SNACKBAR_ERROR_SUPPORT_CLICK, {
          exchangeId,
        });
      }

      window.open(supportLinkUrl, "_blank", "noopener,noreferrer");
    }
  }, [
    incorrectPermission,
    exchangeId,
    supportLinkUrl,
    variant,
    trackEvent,
    setOpenConnectModal,
    setFlowType,
    setExchangeIds,
    setActiveStep,
  ]);
  const isClickable = incorrectPermission || Boolean(supportLinkUrl);

  const icon = getIcon(variant);
  const accentColorClass = getAccentColorClass(variant);
  const backgroundColorClass = getBackgroundColorClass(variant);
  const borderColorClass = getBorderColorClass(variant);

  const contentProps: SnackbarContentProps & React.RefAttributes<HTMLDivElement> = {
    ref,
    role: "alert",
    className,
    style,
  };

  return (
    <SnackbarContent {...contentProps}>
      <div
        {...(isClickable
          ? {
              role: "button",
              tabIndex: 0,
              onClick: handleSnackbarClick,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSnackbarClick();
                }
              },
            }
          : {})}
        className={cn(
          "flex relative rounded-small min-w-[300px] max-w-[600px] overflow-hidden bg-cede-bg-primary",
          isClickable ? "cursor-pointer" : "cursor-default",
        )}
      >
        {/* Background overlay */}
        <div className={cn("absolute inset-0 pointer-events-none", backgroundColorClass)} />

        {/* Accent Bar */}
        <div className={cn("w-1 absolute top-0 bottom-0 left-0", borderColorClass)} />

        <div className="flex items-center p-4 gap-1.5 w-full relative">
          {icon && <div className={cn("flex items-center pt-0.5", accentColorClass)}>{icon}</div>}
          <div className="flex-1">
            <p className="text-body2 text-cede-text-primary">
              {incorrectPermission ? CONNECT_ACCOUNT_SNACKBAR_CONTENT.title(exchangeId ?? "") : message}
            </p>
            {!incorrectPermission && supportText && (
              <p className="text-body2 text-cede-text-secondary mt-0.5">{supportText}</p>
            )}
            {variant === "trade" && <SnackbarTradeInfo />}
            {variant === "error" && incorrectPermission && (
              <p className="text-body2 text-cede-text-secondary mt-0.5">{CONNECT_ACCOUNT_SNACKBAR_CONTENT.subtitle}</p>
            )}
          </div>
          {/* Render custom action if provided */}
          {action && <div className="flex items-center">{typeof action === "function" ? action(id) : action}</div>}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="h-8 w-8 text-cede-text-secondary hover:text-cede-text-primary"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </SnackbarContent>
  );
});
